/**
 * Checkout funnel analytics - rastreia eventos da página de checkout (cobrança PIX/boleto).
 * Foco em erros de carregamento e API para debug (ex: webview Instagram).
 */

import { Platform } from 'react-native';
import * as Sentry from '@sentry/react-native';
import api from '@/services/api';
import { getAttributionFromCookie } from '@/lib/attribution';

export type CheckoutAnalyticsEvent =
  | 'checkout_page_view'
  | 'checkout_load_success'
  | 'checkout_load_error';

export interface CheckoutAnalyticsPayload {
  event: CheckoutAnalyticsEvent;
  chargeId?: string;
  errorMessage?: string;
  httpStatus?: number;
  sessionId?: string;
  utmSource?: string;
  utmCampaign?: string;
}

const SESSION_ID_KEY = 'checkout_analytics_session';

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return '';
  let sid = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sid) {
    sid = `chk_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    sessionStorage.setItem(SESSION_ID_KEY, sid);
  }
  return sid;
}

function getAttributionContext(): Partial<CheckoutAnalyticsPayload> {
  if (typeof window === 'undefined') return {};
  const att = getAttributionFromCookie();
  if (!att) return {};
  return {
    utmSource: att.utmSource ?? undefined,
    utmCampaign: att.utmCampaign ?? undefined,
  };
}

export function trackCheckoutEvent(payload: CheckoutAnalyticsPayload): void {
  Sentry.addBreadcrumb({
    category: 'checkout',
    message: payload.event,
    level: payload.event === 'checkout_load_error' ? 'error' : 'info',
    data: { chargeId: payload.chargeId, httpStatus: payload.httpStatus },
  });

  if (__DEV__) return;
  if (Platform.OS !== 'web') return;

  const sessionId = getOrCreateSessionId();
  const attribution = getAttributionContext();

  const body = {
    event: payload.event,
    errorField: payload.chargeId ? `chargeId=${payload.chargeId}` : undefined,
    errorMessage: payload.errorMessage,
    httpStatus: payload.httpStatus,
    sessionId,
    ...attribution,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };

  api.post('/analytics/register-events', body).catch(() => {});
}
